import React, { createContext, useContext } from 'react';
import axios, { AxiosHeaders, AxiosInstance, AxiosRequestConfig } from 'axios';
import { AuthContext } from './authContext';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { appUrl, basicApiKey, newApiUrl } from '../conf';
import { useAuthService } from '../services/auth/authService';

export type AxiosContentType = {
  authAxios: AxiosInstance;
  publicAxios: AxiosInstance;
  newAuthAxios: AxiosInstance;
};

const AxiosContext = createContext<AxiosContentType>({
  authAxios: axios.create(),
  publicAxios: axios.create(),
  newAuthAxios: axios.create(),
});

export interface AxiosProviderProps {
  children: React.ReactNode;
}

const AxiosProvider: React.FC<AxiosProviderProps> = ({ children }) => {
  const authContext = useContext(AuthContext);
  const authService = useAuthService();
  const authAxios = axios.create({
    baseURL: appUrl,
  });

  const publicAxios = axios.create({
    baseURL: appUrl,
  });

  const newAuthAxios = axios.create({
    baseURL: newApiUrl,
  });

  authAxios.interceptors.request.use(
    config => {
      if (config.headers && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${authContext.getAccessToken()}`;
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  publicAxios.interceptors.request.use(
    config => {
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  newAuthAxios.interceptors.request.use(
    config => {
      config.headers = config.headers || new AxiosHeaders();
      const existingAuth =
        config.headers.Authorization || config.headers.authorization;
      if (!existingAuth) {
        const token = authContext.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  const refreshAuthLogic = (failedRequest: any) => {
    const data = {
      refreshToken: authContext.getRefreshToken(),
    };

    const options: AxiosRequestConfig = {
      method: 'POST',
      data,
      url: `${newApiUrl}/api/auth/refresh`,
      headers: {
        'x-basic-api-key': basicApiKey,
      },
    };

    return axios(options)
      .then(async tokenRefreshResponse => {
        const data = tokenRefreshResponse.data;

        failedRequest.response.config.headers.Authorization =
          'Bearer ' + data.accessToken;

        const { data: userData } = await authService.findUserData();

        await authContext.login(
          data.accessToken,
          data.refreshToken,
          userData.data._id
        );

        return Promise.resolve();
      })
      .catch(e => {
        if (e.message.includes('401')) {
          authContext.logout();
        }
      });
  };

  createAuthRefreshInterceptor(authAxios, refreshAuthLogic, {
    statusCodes: [401],
  });

  createAuthRefreshInterceptor(newAuthAxios, refreshAuthLogic, {
    statusCodes: [401],
  });

  return (
    <AxiosContext.Provider
      value={{
        authAxios,
        publicAxios,
        newAuthAxios,
      }}>
      {children}
    </AxiosContext.Provider>
  );
};

export { AxiosContext, AxiosProvider };
